import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight, ChevronDown, MapPin } from "lucide-react";
import StoreButtons from "@/components/StoreButtons";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay, testid }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className="block will-change-transform"
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
        data-testid={testid}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (e, href) => {
    e.preventDefault();
    if (lenis) lenis.scrollTo(href, { offset: -72, duration: 1.5 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="https://images.unsplash.com/photo-1671245166700-f61e4f26d890?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxuZXclMjB5b3JrJTIwdGF4aSUyMHJpZGVzaGFyZSUyMHVyYmFuJTIwbmlnaHR8ZW58MHx8fHwxNzg5MTQyNjE5fDA&ixlib=rb-4.1.0&q=85"
          alt="City street at night"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-[#050505]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,5,5,0.65)_100%)]" />
      </motion.div>

      <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] bg-brand-yellow/15 rounded-full blur-[140px] z-[1] pointer-events-none" />
      <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] bg-brand-green/10 rounded-full blur-[140px] z-[1] pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-40 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
          className="inline-flex items-center gap-2 rounded-full border border-brand-yellow/40 bg-brand-yellow/10 backdrop-blur-sm px-4 py-1.5 mb-8"
          data-testid="hero-badge"
        >
          <MapPin className="w-3.5 h-3.5 text-brand-yellow" />
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-brand-yellow">
            Now launching in Albany, NY
          </span>
        </motion.div>

        <h1 className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-[12vw] sm:text-[11vw] lg:text-[7.2rem]">
          <MaskedLine delay={0.35} testid="hero-headline-line-1">
            Riders Big <span className="text-brand-yellow">Savings.</span>
          </MaskedLine>
          <MaskedLine delay={0.5} testid="hero-headline-line-2">
            Drivers Huge <span className="text-brand-green">Earnings.</span>
          </MaskedLine>
        </h1>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8 md:gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
            className="max-w-md text-zinc-300 text-base md:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            EzRueda is the Capital Region's own rideshare — lower fares for riders,
            higher take-home for drivers, and every trip rooted in Albany.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1 }}
            className="flex flex-col items-start gap-7"
          >
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#rider-signup"
                onClick={(e) => go(e, "#rider-signup")}
                data-testid="hero-cta-rider"
                className="group inline-flex items-center gap-3 rounded-full bg-brand-yellow text-black font-bold px-8 py-4 text-sm md:text-base hover:bg-brand-yellow-hover hover:scale-105 hover:shadow-[0_0_40px_rgba(255,204,0,0.35)] transition-all duration-300"
              >
                Ride with EzRueda
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <a
                href="#driver-signup"
                onClick={(e) => go(e, "#driver-signup")}
                data-testid="hero-cta-driver"
                className="group inline-flex items-center gap-3 rounded-full border border-brand-green/60 text-brand-green font-bold px-8 py-4 text-sm md:text-base hover:bg-brand-green hover:text-black hover:scale-105 hover:shadow-[0_0_40px_rgba(0,200,83,0.3)] transition-all duration-300"
              >
                Drive &amp; Earn
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
            <div className="flex flex-col gap-3">
              <p
                className="text-[11px] font-bold tracking-[0.25em] uppercase text-zinc-500"
                data-testid="hero-store-caption"
              >
                Get the app — launching soon
              </p>
              <StoreButtons idPrefix="hero" />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-8 md:mt-16 flex justify-center"
        >
          <ChevronDown className="w-5 h-5 text-zinc-500 animate-bounce" data-testid="hero-scroll-cue" />
        </motion.div>
      </motion.div>
    </section>
  );
}
